import { PollVoteChoice } from "@/api/types";
import { cn } from "@/lib/utils";

/**
 * One row per proposed time, each with a three-button Yes / Less preferred / No control.
 *
 * <p>Shared by the voting page and the amend page, because they are the same act: the second one
 * just arrives with the answers already filled in.</p>
 *
 * <p><b>Nothing is selected by default.</b> Pre-setting No would fabricate an opinion nobody
 * expressed and poison the tallies, and pre-setting Yes would do the same in the other direction.
 * An unanswered row is a real, meaningful state — the host's grid shows it as "no answer", not as a
 * rejection — so the ballot has to be able to represent it.</p>
 */

const DAY = new Intl.DateTimeFormat(undefined, { weekday: "long", day: "numeric", month: "long" });
const TIME = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" });

export interface BallotRow {
  id: string;
  start_at: string;
  end_at: string;
  /** Counts, shown only when the host allows it and this person has already voted. */
  yes?: number | null;
  less_preferred?: number | null;
  no?: number | null;
}

const CHOICES: { value: PollVoteChoice; label: string; selected: string }[] = [
  // White on #ECD06F fails contrast, so Less preferred keeps foreground text on its fill.
  { value: "YES", label: "Yes", selected: "bg-brand-teal text-white" },
  { value: "LESS_PREFERRED", label: "Less preferred", selected: "bg-brand-yellow text-foreground" },
  { value: "NO", label: "No", selected: "bg-brand-coral text-white" },
];

export function PollBallot({
  rows,
  answers,
  onAnswer,
  showCounts,
  readOnly,
  highlightMissing,
  confirmedOptionId,
}: {
  rows: BallotRow[];
  /** option id → choice. A missing key is "not answered", which is a state, not an absence. */
  answers: Record<string, PollVoteChoice>;
  onAnswer: (optionId: string, choice: PollVoteChoice) => void;
  showCounts?: boolean;
  /** Voting is over, or this is the receipt: the choices render but cannot be changed. */
  readOnly?: boolean;
  /** After a submit attempt with gaps, outline the rows that still have no answer. */
  highlightMissing?: boolean;
  confirmedOptionId?: string | null;
}) {
  return (
    <ul className="space-y-2">
      {rows.map((row) => {
        const chosen = answers[row.id];
        const missing = highlightMissing && !chosen;
        const isConfirmed = confirmedOptionId === row.id;
        return (
          <li
            key={row.id}
            className={cn(
              "flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-xl border p-3",
              missing ? "border-brand-coral" : "border-border",
              isConfirmed && "bg-brand-teal/10",
            )}
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">{DAY.format(new Date(row.start_at))}</p>
              <p className="text-sm text-foreground">
                {TIME.format(new Date(row.start_at))}
                {"–"}
                {TIME.format(new Date(row.end_at))}
                {isConfirmed && <span className="ml-2 font-medium text-brand-teal">confirmed</span>}
              </p>
              {showCounts && (
                <p className="mt-0.5 text-sm text-foreground">
                  {row.yes ?? 0} yes · {row.less_preferred ?? 0} less preferred · {row.no ?? 0} no
                </p>
              )}
            </div>

            {/* One bordered pill group. It reads correctly with no caption above it, which is why
                there is none. */}
            <div
              role="radiogroup"
              aria-label={`Your answer for ${DAY.format(new Date(row.start_at))} ${TIME.format(new Date(row.start_at))}`}
              className="inline-flex shrink-0 overflow-hidden rounded-full border border-foreground text-sm font-medium"
            >
              {CHOICES.map((c, i) => (
                <button
                  key={c.value}
                  type="button"
                  role="radio"
                  aria-checked={chosen === c.value}
                  disabled={readOnly}
                  onClick={() => onAnswer(row.id, c.value)}
                  className={cn(
                    "px-3 py-1.5 transition",
                    i > 0 && "border-l border-foreground",
                    chosen === c.value ? c.selected : "text-foreground hover:bg-muted",
                    readOnly && "cursor-default",
                  )}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
